import { articleApi, ArticleStatus, articleStatusMap, companyApi, CompanyImageVo, ICompanyArticle } from '@/axios/company';
import { openConfirmModal, openDeleteModal } from '@/components/Modal/common';
import { useRequest, useSetState } from 'ahooks';
import {
  Button,
  Checkbox,
  DatePicker,
  Empty,
  Flex,
  Form,
  Input,
  InputNumber,
  message,
  Popover,
  Select,
  Space,
  Tooltip,
  Typography,
  Upload,
} from 'antd';
import { ColumnsType } from 'antd/es/table';
import { FilterValue, SorterResult, TablePaginationConfig } from 'antd/es/table/interface';
import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import type { UIEvent } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import styles from './index.module.less';
import CommonModalFactory from '@/plugins/ModalFactory';
import markdownConfig from '../markdownConfig';
import { useNavigate } from 'react-router-dom';
import SearchInput from '@/components/SearchInput';
import OperatorBtns from '@/components/Table/operateBtns';
import EllipsisText from '@/components/Table/EllipsisText';
import CommonTable from '@/components/Table';
import { DragDropContext, Draggable, Droppable, type DropResult } from '@/components/ReactBeautifulDnd';
import AccessButton from '@/components/AccessButton';
import ChannelMediaBoxSelector from '@/components/Form/ChannelMediaBoxSelector';
import SVGIcon from '@/components/SVGIcon';
import TipIcon from '@/assets/imgs/tip.svg?react';
import useResetToFirstPageOrRefresh from '@/hooks/useResetToFirstPageOrRefresh';
import { timeFormatToYearSecond } from '@/utils/date';
import { EPublishStatus, getPublishStatusText, isPublishedStatus } from '@/constants/geo';
import {
  AlignCenterOutlined,
  AlignLeftOutlined,
  AlignRightOutlined,
  AppstoreOutlined,
  CheckSquareOutlined,
  DeleteOutlined,
  HolderOutlined,
  InfoCircleOutlined,
  PictureOutlined,
  UploadOutlined,
} from '@ant-design/icons';

const { Text } = Typography;
const { RangePicker } = DatePicker;

type ArticleImageAlign = 'left' | 'center' | 'right';
type ArticleImageSize = 'small' | 'medium' | 'large' | 'original';

type ArticleImageBlock = {
  id: string;
  block: string;
  src: string;
  alt: string;
  align: ArticleImageAlign;
  size: ArticleImageSize;
  width?: number;
  from: {
    line: number;
    ch: number;
  };
  to: {
    line: number;
    ch: number;
  };
};

type ArticleImageMenu = {
  block: ArticleImageBlock;
  left: number;
  top: number;
};

const IMAGE_BLOCK_REG =
  /<(?:div|figure)\s+class="geo-article-image"[^>]*data-align="(left|center|right)"[^>]*data-size="(small|medium|large|original)"(?:[^>]*data-width="(\d+)")?[^>]*>\s*<img\s+src="([^"]+)"\s+alt="([^"]*)"[^>]*\/?>\s*<\/(?:div|figure)>/g;
const ACCEPT_IMAGE_TYPES = '.jpg,.jpeg,.png';
const MAX_IMAGE_SIZE = 10 * 1024 * 1024;
const IMAGE_RENDER_BATCH_SIZE = 20;
const ARTICLE_COLUMN_SETTINGS_STORAGE_KEY = 'geo-article-list-column-settings-v1';

type ArticleColumnSettings = {
  order: string[];
  hidden: string[];
};

type ColumnSettingItem = {
  key: string;
  label: string;
};

const getDefaultColumnSettings = (columnKeys: string[] = []): ArticleColumnSettings => ({
  order: [],
  hidden: columnKeys.slice(5),
});

const loadColumnSettings = (storageKey: string, columnKeys: string[]): ArticleColumnSettings => {
  try {
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) {
      return getDefaultColumnSettings(columnKeys);
    }
    const parsed = JSON.parse(raw) as Partial<ArticleColumnSettings>;
    return {
      order: Array.isArray(parsed.order) ? parsed.order.filter((item): item is string => typeof item === 'string') : [],
      hidden: Array.isArray(parsed.hidden)
        ? parsed.hidden.filter((item): item is string => typeof item === 'string')
        : [],
    };
  } catch {
    return getDefaultColumnSettings(columnKeys);
  }
};

const saveColumnSettings = (storageKey: string, settings: ArticleColumnSettings) => {
  try {
    window.localStorage.setItem(storageKey, JSON.stringify(settings));
  } catch {
    // Ignore unavailable or full browser storage; the current session still keeps the setting.
  }
};

const ColumnSettingsPopover = ({
  items,
  settings,
  onChange,
}: {
  items: ColumnSettingItem[];
  settings: ArticleColumnSettings;
  onChange: (settings: ArticleColumnSettings) => void;
}) => {
  const itemKeys = items.map((item) => item.key);
  const orderedKeys = [
    ...settings.order.filter((key) => itemKeys.includes(key)),
    ...itemKeys.filter((key) => !settings.order.includes(key)),
  ];
  const itemMap = new Map(items.map((item) => [item.key, item]));
  const orderedItems = orderedKeys.map((key) => itemMap.get(key)).filter((item): item is ColumnSettingItem => !!item);
  const hiddenKeys = new Set(settings.hidden);
  const visibleCount = orderedItems.filter((item) => !hiddenKeys.has(item.key)).length;

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination || result.source.index === result.destination.index) {
      return;
    }
    const nextOrder = [...orderedKeys];
    const [draggedKey] = nextOrder.splice(result.source.index, 1);
    nextOrder.splice(result.destination.index, 0, draggedKey);
    onChange({ ...settings, order: nextOrder });
  };

  return (
    <div className={styles.columnSettings}>
      <div className={styles.columnSettingsHeader}>
        <span>字段设置</span>
        <Button type="link" size="small" onClick={() => onChange(getDefaultColumnSettings(itemKeys))}>
          重置
        </Button>
      </div>
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="article-column-settings">
          {(provided: any) => (
            <div ref={provided.innerRef} {...provided.droppableProps} className={styles.columnSettingsList}>
              {orderedItems.map((item, index) => {
                const visible = !hiddenKeys.has(item.key);
                return (
                  <Draggable key={item.key} draggableId={item.key} index={index}>
                    {(dragProvided: any, snapshot: any) => (
                      <div
                        ref={dragProvided.innerRef}
                        {...dragProvided.draggableProps}
                        className={`${styles.columnSettingsItem} ${
                          snapshot.isDragging ? styles.columnSettingsItemDragging : ''
                        }`}
                      >
                        <span
                          {...dragProvided.dragHandleProps}
                          className={styles.columnSettingsHandle}
                          title="拖动排序"
                          aria-label={`${item.label}拖动排序`}
                        >
                          <HolderOutlined />
                        </span>
                        <Checkbox
                          checked={visible}
                          disabled={visible && visibleCount === 1}
                          onChange={(event) => {
                            const nextHidden = event.target.checked
                              ? settings.hidden.filter((key) => key !== item.key)
                              : Array.from(new Set([...settings.hidden, item.key]));
                            onChange({ ...settings, hidden: nextHidden });
                          }}
                        >
                          {item.label}
                        </Checkbox>
                      </div>
                    )}
                  </Draggable>
                );
              })}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
    </div>
  );
};

const imageSizeText: Record<ArticleImageSize, string> = {
  small: '小尺寸',
  medium: '中尺寸',
  large: '大尺寸',
  original: '原始尺寸',
};

const imageAlignText: Record<ArticleImageAlign, string> = {
  left: '左对齐',
  center: '居中对齐',
  right: '右对齐',
};

const escapeHtmlAttr = (value?: string) =>
  (value || '')
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

const buildArticleImageBlock = (
  image: Pick<CompanyImageVo, 'imageUrl' | 'imageName'>,
  align: ArticleImageAlign = 'center',
  size: ArticleImageSize = 'medium',
  width?: number
) => {
  const widthAttr = width ? ` data-width="${width}"` : '';
  const widthStyle = width ? ` style="width: ${width}px;"` : '';
  return `<figure class="geo-article-image" data-align="${align}" data-size="${size}"${widthAttr}>
  <img src="${escapeHtmlAttr(image.imageUrl)}" alt="${escapeHtmlAttr(image.imageName)}"${widthStyle} />
  </figure>`;
};

const buildMarkdownInsertion = (markdown: string, startIndex: number, endIndex: number, snippet: string) => {
  const content = snippet.trim();
  const before = markdown.slice(0, startIndex);
  const after = markdown.slice(endIndex);
  const prefix = !before ? '' : before.endsWith('\n\n') ? '' : before.endsWith('\n') ? '\n' : '\n\n';
  const suffix = !after ? '\n' : after.startsWith('\n\n') ? '' : after.startsWith('\n') ? '\n' : '\n\n';
  return `${prefix}${content}${suffix}`;
};

const parseArticleImageBlocks = (markdown = ''): ArticleImageBlock[] => {
  const blocks: ArticleImageBlock[] = [];
  markdown.replace(IMAGE_BLOCK_REG, (block, align: ArticleImageAlign, size: ArticleImageSize, width, src, alt, startIndex) => {
    const beforeLines = markdown.slice(0, startIndex).split('\n');
    const blockLines = block.split('\n');
    const from = {
      line: beforeLines.length - 1,
      ch: beforeLines[beforeLines.length - 1].length,
    };
    const to = {
      line: from.line + blockLines.length - 1,
      ch: blockLines.length === 1 ? from.ch + block.length : blockLines[blockLines.length - 1].length,
    };
    blocks.push({
      id: `${src}-${blocks.length}`,
      block,
      align,
      size,
      width: width ? Number(width) : undefined,
      src,
      alt,
      from,
      to,
    });
    return block;
  });
  return blocks;
};

const getImagePreviewClassName = (align: ArticleImageAlign, size: ArticleImageSize) =>
  `${styles.articleEditorImagePreview} ${styles[`articleEditorImagePreview_${align}`]} ${styles[`articleEditorImagePreview_${size}`]}`;

const createEditorImagePreviewNode = (
  block: ArticleImageBlock,
  onMouseDown?: (block: ArticleImageBlock, event: MouseEvent) => void,
  onResizeMouseDown?: (block: ArticleImageBlock, image: HTMLImageElement, event: MouseEvent) => void
) => {
  const wrapper = document.createElement('div');
  wrapper.className = getImagePreviewClassName(block.align, block.size);
  wrapper.title = block.alt || '已插入图片';
  wrapper.onclick = (event) => {
    event.preventDefault();
    event.stopPropagation();
  };
  wrapper.onmousedown = (event) => {
    onMouseDown?.(block, event);
  };
  const image = document.createElement('img');
  image.src = block.src;
  image.alt = block.alt;
  if (block.width) {
    image.style.width = `${block.width}px`;
  }
  wrapper.appendChild(image);
  const resizeHandle = document.createElement('span');
  resizeHandle.className = styles.articleEditorImageResizeHandle;
  resizeHandle.onmousedown = (event) => {
    onResizeMouseDown?.(block, image, event);
  };
  wrapper.appendChild(resizeHandle);
  return wrapper;
};

const getMarkdownLineStartIndex = (markdown: string, line: number) => {
  if (line <= 0) {
    return 0;
  }
  const lines = markdown.split('\n');
  return lines.slice(0, Math.min(line, lines.length)).reduce((sum, item) => sum + item.length + 1, 0);
};

const insertImageBlockAtIndex = (markdown: string, block: string, index: number) => {
  const before = markdown.slice(0, index).replace(/\s*$/, '');
  const after = markdown.slice(index).replace(/^\s*/, '');
  return [before, block, after].filter(Boolean).join('\n\n');
};

const getReferenceCount = (record: ICompanyArticle) => {
  return record.referenceCount ?? record.quoteCount ?? record.quotedCount ?? record.citedCount ?? '-';
};

type ArticleListParams = {
  pageIndex: number;
  pageSize: number;
  companyId: string;
  search: string;
  status: ArticleStatus[];
  publishStatus: EPublishStatus[];
  mediaIds: number[];
  mediaNames: string[];
  priceMin?: number | null;
  priceMax?: number | null;
  referenceCountMin?: number | null;
  referenceCountMax?: number | null;
  generateTimeStart?: string | null;
  generateTimeEnd?: string | null;
};
type ArticleListParamsUpdater = (params: Partial<ArticleListParams>) => void;

const ArticleList = forwardRef(
  (
    {
      btns,
      from = 'company',
      externalParams,
      onParamsChange,
      buildCompanyDetailUrl,
    }: {
      btns?: React.ReactNode;
      from?: 'all' | 'company';
      externalParams?: ArticleListParams;
      onParamsChange?: ArticleListParamsUpdater;
      buildCompanyDetailUrl?: (companyId: string | number) => string;
    },
    ref
  ) => {
    const navigate = useNavigate();
    const columnSettingsStorageKey = `${ARTICLE_COLUMN_SETTINGS_STORAGE_KEY}:${from}`;
    const companyId = new URLSearchParams(window.location.search).get('companyId') || '';
    // 文章列表
    const [innerArticleParams, setInnerArticleParams] = useSetState<ArticleListParams>({
      pageIndex: 1,
      pageSize: 10,
      companyId,
      search: '',
      status: [],
      publishStatus: [],
      mediaIds: [],
      mediaNames: [],
      priceMin: undefined,
      priceMax: undefined,
      referenceCountMin: undefined,
      referenceCountMax: undefined,
      generateTimeStart: undefined,
      generateTimeEnd: undefined,
    });
    const updateInnerArticleParams: ArticleListParamsUpdater = (params) => {
      setInnerArticleParams(params as Partial<ArticleListParams> & ArticleListParams);
    };
    const articleParams = externalParams || innerArticleParams;
    const setArticleParams = onParamsChange || updateInnerArticleParams;
    const {
      data: articleList,
      runAsync: refreshArticleList,
      loading,
    } = useRequest(
      () =>
        articleApi.list({
          ...articleParams,
          priceMin: articleParams.priceMin ?? undefined,
          priceMax: articleParams.priceMax ?? undefined,
          referenceCountMin: articleParams.referenceCountMin ?? undefined,
          referenceCountMax: articleParams.referenceCountMax ?? undefined,
          generateTimeStart: articleParams.generateTimeStart ?? undefined,
          generateTimeEnd: articleParams.generateTimeEnd ?? undefined,
        }),
      {
        refreshDeps: [articleParams],
      }
    );
    const { data: mediaOptions } = useRequest(() => articleApi.listPublishedMediaOptions());
    const resetToFirstPageOrRefresh = useResetToFirstPageOrRefresh(articleParams, setArticleParams, refreshArticleList);
    const [tempReferenceCountMin, setTempReferenceCountMin] = useState<number | null>(
      articleParams.referenceCountMin ?? null
    );
    const [tempReferenceCountMax, setTempReferenceCountMax] = useState<number | null>(
      articleParams.referenceCountMax ?? null
    );
    useEffect(() => {
      setTempReferenceCountMin(articleParams.referenceCountMin ?? null);
      setTempReferenceCountMax(articleParams.referenceCountMax ?? null);
    }, [articleParams.referenceCountMin, articleParams.referenceCountMax]);
    const [tempPriceMin, setTempPriceMin] = useState<number>(articleParams.priceMin ?? 0);
    const [tempPriceMax, setTempPriceMax] = useState<number | null>(articleParams.priceMax ?? null);
    useEffect(() => {
      setTempPriceMin(articleParams.priceMin ?? 0);
      setTempPriceMax(articleParams.priceMax ?? null);
    }, [articleParams.priceMin, articleParams.priceMax]);
    const [tempGenerateTimeRange, setTempGenerateTimeRange] = useState<[Dayjs | null, Dayjs | null] | null>([
      articleParams.generateTimeStart ? dayjs(articleParams.generateTimeStart) : null,
      articleParams.generateTimeEnd ? dayjs(articleParams.generateTimeEnd) : null,
    ]);
    useEffect(() => {
      setTempGenerateTimeRange([
        articleParams.generateTimeStart ? dayjs(articleParams.generateTimeStart) : null,
        articleParams.generateTimeEnd ? dayjs(articleParams.generateTimeEnd) : null,
      ]);
    }, [articleParams.generateTimeStart, articleParams.generateTimeEnd]);
    const mediaFilterOptions = (mediaOptions || []).map((item) => ({
      value: item.mediaName,
      label: item.channelType ? `${item.mediaName}(${item.channelType})` : item.mediaName,
    }));
    const [tempMediaNames, setTempMediaNames] = useState<string[]>(articleParams.mediaNames || []);
    useEffect(() => {
      setTempMediaNames(articleParams.mediaNames || []);
    }, [articleParams.mediaNames]);
    // 重新生成
    const [_, setOperating] = useState<{ id: string | null; type: 'retry' | 'copy' | 'download' } | null>(null);
    const handleRetry = (articleId: string) => {
      setOperating({ id: articleId, type: 'retry' });
      articleApi
        .retry(articleId)
        .then(() => {
          message.success('已重新生成，请稍后查看');
          refreshArticleList();
        })
        .finally(() => {
          setOperating(null);
        });
    };
    const columns: ColumnsType<ICompanyArticle> = [
      ...(from === 'all'
        ? [
            {
              title: '企业',
              dataIndex: 'companyName',
              width: 200,
              ellipsis: true,
              render: (value: string, record: ICompanyArticle) => (
                <a
                  onClick={() => {
                    navigate(buildCompanyDetailUrl?.(record.companyId) || `/articles?companyId=${record.companyId}`);
                  }}
                >
                  <EllipsisText text={value} />
                </a>
              ),
            },
          ]
        : []),
      {
        title: '文章标题',
        dataIndex: 'title',
        width: 200,
        ellipsis: true,
        render: (value: string, record: ICompanyArticle) => {
          return (
            <Text
              ellipsis={{
                tooltip: value,
              }}
            >
              <a href={`/api/article/${record.articleId}.html`} target="_blank" rel="noopener noreferrer">{value}</a>
            </Text>
          );
        },
      },
      {
        title: '第三方订单ID',
        dataIndex: 'thirdPartyOrderId',
        width: 180,
        render: (value?: string) => <EllipsisText text={value || '-'} />,
      },
      {
        title: '文章模板',
        dataIndex: 'templateName',
        width: 120,
        render: (value?: string) => <EllipsisText text={value ?? '-'} />,
      },
      {
        title: '媒体名称',
        dataIndex: 'mediaName',
        width: 160,
        filtered: articleParams.mediaNames.length > 0,
        filterDropdown: ({ confirm, clearFilters }) => (
          <div style={{ padding: 8, width: 280 }}>
            <Flex vertical gap={8}>
              <Select
                mode="multiple"
                allowClear
                showSearch
                optionFilterProp="label"
                maxTagCount="responsive"
                placeholder="筛选媒体名称"
                value={tempMediaNames}
                options={mediaFilterOptions}
                onChange={(value) => setTempMediaNames(value)}
                style={{ width: '100%' }}
              />
              <Space>
                <Button
                  type="primary"
                  size="small"
                  onClick={() => {
                    setArticleParams({ mediaNames: tempMediaNames, mediaIds: [], pageIndex: 1 });
                    confirm();
                  }}
                >
                  确定
                </Button>
                <Button
                  size="small"
                  onClick={() => {
                    setTempMediaNames([]);
                    setArticleParams({ mediaNames: [], mediaIds: [], pageIndex: 1 });
                    clearFilters?.();
                  }}
                >
                  重置
                </Button>
              </Space>
            </Flex>
          </div>
        ),
        render: (value: string | undefined, record: ICompanyArticle) => {
          const platform = record.platformName;
          const mediaName = value || '-';
          return <EllipsisText text={platform ? `${mediaName}(${platform})` : mediaName} />;
        },
      },
      {
        title: '文章类型',
        dataIndex: 'categoryName',
        width: 120,
        render: (value: string) => <EllipsisText text={value} />,
      },
      {
        title: '使用竞品',
        dataIndex: 'competitors',
        ellipsis: true,
        width: 200,
        render: (value: string[]) => {
          const competitors = value?.filter(Boolean) || [];
          const text = competitors.length > 0 ? competitors.join('、') : '-';
          return (
            <Text
              ellipsis={{
                tooltip: text,
              }}
            >
              {text}
            </Text>
          );
        },
      },
      {
        title: '价格',
        dataIndex: 'price',
        width: 120,
        filtered: (articleParams.priceMin != null && articleParams.priceMin !== 0) || articleParams.priceMax != null,
        filterDropdown: ({ confirm, clearFilters }) => (
          <div style={{ padding: 8 }}>
            <Flex vertical gap={8} style={{ width: '100%' }}>
              <Space>
                <InputNumber<number>
                  min={0}
                  step={1}
                  precision={0}
                  controls
                  value={tempPriceMin}
                  onChange={(value) => setTempPriceMin(value ?? 0)}
                  style={{ width: 120 }}
                />
                <span>-</span>
                <InputNumber<number>
                  min={0}
                  step={1}
                  precision={0}
                  controls
                  value={tempPriceMax}
                  onChange={(value) => setTempPriceMax(value ?? null)}
                  style={{ width: 120 }}
                />
              </Space>
              <Space>
                <Button
                  type="primary"
                  size="small"
                  onClick={() => {
                    if (tempPriceMax !== null && tempPriceMin > tempPriceMax) {
                      message.warning('最低价格不能大于最高价格');
                      return;
                    }
                    setArticleParams({
                      pageIndex: 1,
                      priceMin: tempPriceMin === 0 ? null : tempPriceMin,
                      priceMax: tempPriceMax,
                    });
                    confirm();
                  }}
                >
                  确定
                </Button>
                <Button
                  size="small"
                  onClick={() => {
                    setTempPriceMin(0);
                    setTempPriceMax(null);
                    setArticleParams({
                      pageIndex: 1,
                      priceMin: null,
                      priceMax: null,
                    });
                    clearFilters?.();
                  }}
                >
                  重置
                </Button>
              </Space>
            </Flex>
          </div>
        ),
        render: (value?: number | string | null) => value ?? '-',
      },
      {
        title: '文章生成时间',
        dataIndex: 'generateTime',
        width: 180,
        filtered: !!articleParams.generateTimeStart || !!articleParams.generateTimeEnd,
        filterDropdown: ({ confirm, clearFilters }) => (
          <div style={{ padding: 8 }}>
            <Flex vertical gap={8}>
              <RangePicker
                allowEmpty={[true, true]}
                format="YYYY-MM-DD"
                value={tempGenerateTimeRange}
                onChange={(value) => {
                  setTempGenerateTimeRange((value as [Dayjs | null, Dayjs | null] | null) ?? null);
                }}
              />
              <Space>
                <Button
                  type="primary"
                  size="small"
                  onClick={() => {
                    setArticleParams({
                      pageIndex: 1,
                      generateTimeStart: tempGenerateTimeRange?.[0]?.format('YYYY-MM-DD') ?? null,
                      generateTimeEnd: tempGenerateTimeRange?.[1]?.format('YYYY-MM-DD') ?? null,
                    });
                    confirm();
                  }}
                >
                  确定
                </Button>
                <Button
                  size="small"
                  onClick={() => {
                    setTempGenerateTimeRange(null);
                    setArticleParams({
                      pageIndex: 1,
                      generateTimeStart: null,
                      generateTimeEnd: null,
                    });
                    clearFilters?.();
                  }}
                >
                  重置
                </Button>
              </Space>
            </Flex>
          </div>
        ),
        render: (_, record) => {
          const time = record.generateTime || record.createTime;
          if (!time) {
            return '-';
          }
          const normalizedTime = time instanceof Date ? time.toISOString() : String(time);
          return <div style={{ whiteSpace: 'pre-wrap' }}>{timeFormatToYearSecond(normalizedTime)}</div>;
        },
      },
      {
        title: '状态',
        dataIndex: 'status',
        width: 120,
        filters: Object.entries(articleStatusMap).map(([value, label]) => ({
          value: value as ArticleStatus,
          text: label,
        })),
        filteredValue: articleParams.status.length > 0 ? articleParams.status : null,
        render: (value: ICompanyArticle['status']) => {
          return <>{articleStatusMap[value] || '-'}</>;
        },
      },
      {
        title: '被引用次数',
        dataIndex: 'referenceCount',
        width: 120,
        filtered: articleParams.referenceCountMin != null || articleParams.referenceCountMax != null,
        filterDropdown: ({ confirm, clearFilters }) => (
          <div style={{ padding: 8 }}>
            <Flex vertical gap={8}>
              <Space>
                <InputNumber<number>
                  placeholder="最小值"
                  min={0}
                  step={1}
                  precision={0}
                  value={tempReferenceCountMin}
                  onChange={(value) => setTempReferenceCountMin(value ?? null)}
                  style={{ width: 120 }}
                />
                <span>-</span>
                <InputNumber<number>
                  placeholder="最大值"
                  min={0}
                  step={1}
                  precision={0}
                  value={tempReferenceCountMax}
                  onChange={(value) => setTempReferenceCountMax(value ?? null)}
                  style={{ width: 120 }}
                />
              </Space>
              <Space>
                <Button
                  type="primary"
                  size="small"
                  onClick={() => {
                    if (
                      tempReferenceCountMin !== null &&
                      tempReferenceCountMax !== null &&
                      tempReferenceCountMin > tempReferenceCountMax
                    ) {
                      message.warning('最小值不能大于最大值');
                      return;
                    }
                    setArticleParams({
                      pageIndex: 1,
                      referenceCountMin: tempReferenceCountMin,
                      referenceCountMax: tempReferenceCountMax,
                    });
                    confirm();
                  }}
                >
                  确定
                </Button>
                <Button
                  size="small"
                  onClick={() => {
                    setTempReferenceCountMin(null);
                    setTempReferenceCountMax(null);
                    setArticleParams({
                      pageIndex: 1,
                      referenceCountMin: null,
                      referenceCountMax: null,
                    });
                    clearFilters?.();
                  }}
                >
                  重置
                </Button>
              </Space>
            </Flex>
          </div>
        ),
        render: (_, record) => getReferenceCount(record),
      },
      {
        title: '客户后台状态',
        dataIndex: 'publishStatus',
        width: 160,
        filters: [
          { text: '已发布', value: EPublishStatus.PUBLISHED },
          { text: '未发布', value: EPublishStatus.UNPUBLISHED },
        ],
        filteredValue: articleParams.publishStatus.length > 0 ? articleParams.publishStatus : null,
        render: (value: ICompanyArticle['publishStatus']) => getPublishStatusText(value),
      },
      {
        title: '返链',
        dataIndex: 'publishUrl',
        width: 120,
        render: (value?: string) =>
          value ? (
            <a href={value} target="_blank" rel="noopener noreferrer">
              {value}
            </a>
          ) : (
            '-'
          ),
      },
      {
        title: '操作',
        key: 'actions',
        width: 360,
        fixed: 'right',
        render: (_, record) => {
          const isAutoPublish = record.autoPublish;
          const status = record.status;
          let showPublishBtn = false;
          if (status === ArticleStatus.RELEASE_FAIL) {
            // 已拒稿
            showPublishBtn = true;
          }
          if (!isAutoPublish && status === ArticleStatus.WAIT_PUBLISH) {
            // 待发布
            showPublishBtn = true;
          }
          // 已发布状态,已发布待返链
          const isPublished = status === ArticleStatus.RELEASE || status === ArticleStatus.RELEASED;
          const canPublishToClient = status === ArticleStatus.WAIT_PUBLISH || status === ArticleStatus.RELEASED;
          const isPublishFailed = status === ArticleStatus.RELEASE_FAIL;
          const isPublishing = status === ArticleStatus.PUBLISH_ING;
          const isPublishedToClient = isPublishedStatus(record.publishStatus);
          return (
            <OperatorBtns
              btns={[
                {
                  type: 'common',
                  text: '取消发布',
                  onClick: async () => {
                    openConfirmModal(
                      {
                        confirmApi: () => articleApi.unPublish([record.articleId]),
                        content: '确定取消发布该文章吗？',
                      },
                      undefined,
                      () => {
                        message.success('取消发布成功');
                        refreshArticleList();
                      }
                    );
                  },
                  permission: ['company:article:publish'],
                  show: isPublishing,
                },
                {
                  type: 'common',
                  text: isPublishFailed ? (
                    <Tooltip title={record.reason}>
                      <Space>
                        发布
                        <SVGIcon fill style={{
                          color: '#FF4D4F',
                        }} icon={<TipIcon />} />
                      </Space>
                    </Tooltip>
                  ) : (
                    '发布'
                  ),
                  onClick: async () => {
                    openConfirmModal(
                      {
                        confirmApi: () => articleApi.publish([record.articleId]),
                        content: '确定发布该文章吗？',
                      },
                      undefined,
                      () => {
                        message.success('发布成功');
                        refreshArticleList();
                      }
                    );
                  },
                  permission: ['company:article:publish'],
                  show: showPublishBtn,
                },
                {
                  type: 'common',
                  text: isPublishedToClient ? '取消发布至客户后台' : '发布至客户后台',
                  onClick: async () => {
                    if (isPublishedToClient) {
                      await articleApi.unpublishToClient([record.articleId]);
                      message.success('取消发布成功');
                    } else {
                      await articleApi.publishToClient([record.articleId]);
                      message.success('发布成功');
                    }
                    refreshArticleList();
                  },
                  permission: ['company:article:publish'],
                  show: canPublishToClient,
                },
                {
                  type: 'common',
                  text: '复制',
                  onClick: async () => {
                    setOperating({ id: record.articleId, type: 'copy' });
                    try {
                      const res = await articleApi.get(record.articleId);
                      navigator.clipboard.writeText(res.content || '');
                      message.success('已复制到剪切板');
                    } finally {
                      setOperating(null);
                    }
                  },
                  show: from === 'company',
                },
                {
                  type: 'common',
                  text: '重新生成',
                  onClick: () => {
                    handleRetry(record.articleId);
                  },
                  permission: ['company:article:create'],
                  show: !isPublished,
                },
                {
                  type: 'common',
                  text: '下载',
                  onClick: async () => {
                    // markdown转html转docx下载
                    setOperating({ id: record.articleId, type: 'download' });
                    const downloadKey = await articleApi.download([record.articleId]);
                    window.open(`/api/common/download/${downloadKey}`);
                    setOperating(null);
                  },
                },
                {
                  type: 'edit',
                  onClick: () => {
                    openEditArticleModal(
                      {
                        articleId: record.articleId,
                        mediaId: record.mediaId,
                      },
                      undefined,
                      () => {
                        message.success('更新成功');
                        resetToFirstPageOrRefresh();
                      }
                    );
                  },
                  permission: ['company:article:edit'],
                },
                {
                  type: 'delete',
                  onClick: () => {
                    openDeleteModal(
                      {
                        content: '确认删除该文章吗？',
                        api: async () => {
                          // 删除接口调用
                          await articleApi.delete([record.articleId]);
                        },
                      },
                      undefined,
                      resetToFirstPageOrRefresh
                    );
                  },
                  show: from === 'company' && !isPublished,
                  permission: ['company:article:delete'],
                },
                {
                  type: 'common',
                  text: '查看企业',
                  onClick: () => {
                    navigate(buildCompanyDetailUrl?.(record.companyId) || `/articles?companyId=${record.companyId}`);
                  },
                  show: from === 'all',
                  permission: ['company:view'],
                },
              ]}
            />
          );
        },
      },
    ];
    const configurableColumns = columns.filter((column) => column.key !== 'actions');
    const columnSettingItems = configurableColumns
      .map((column) => {
        const dataIndex = 'dataIndex' in column ? column.dataIndex : undefined;
        const key = String(column.key ?? dataIndex ?? '');
        return {
          key,
          label: typeof column.title === 'string' ? column.title : key,
        };
      })
      .filter((item) => !!item.key);
    const availableColumnKeys = columnSettingItems.map((item) => item.key);
    const [columnSettings, setColumnSettings] = useState<ArticleColumnSettings>(() =>
      loadColumnSettings(columnSettingsStorageKey, availableColumnKeys)
    );
    const updateColumnSettings = (nextSettings: ArticleColumnSettings) => {
      setColumnSettings(nextSettings);
      saveColumnSettings(columnSettingsStorageKey, nextSettings);
    };
    const orderedColumnKeys = [
      ...columnSettings.order.filter((key) => availableColumnKeys.includes(key)),
      ...availableColumnKeys.filter((key) => !columnSettings.order.includes(key)),
    ];
    const hiddenColumnKeys = new Set(columnSettings.hidden);
    const columnsByKey = new Map(
      configurableColumns.map((column) => {
        const dataIndex = 'dataIndex' in column ? column.dataIndex : undefined;
        return [String(column.key ?? dataIndex ?? ''), column] as const;
      })
    );
    const actionColumn = columns.find((column) => column.key === 'actions');
    const actionColumnWithSettings = actionColumn
      ? {
          ...actionColumn,
          title: (
            <Flex align="center" justify="space-between">
              <span>操作</span>
              <Popover
                trigger="click"
                placement="bottomRight"
                content={
                  <ColumnSettingsPopover
                    items={columnSettingItems}
                    settings={columnSettings}
                    onChange={updateColumnSettings}
                  />
                }
              >
                <Tooltip title="字段设置">
                  <Button type="text" size="small" icon={<AppstoreOutlined />} aria-label="字段设置" />
                </Tooltip>
              </Popover>
            </Flex>
          ),
        }
      : undefined;
    const displayColumns: ColumnsType<ICompanyArticle> = [
      ...orderedColumnKeys
        .filter((key) => !hiddenColumnKeys.has(key))
        .map((key) => columnsByKey.get(key))
        .filter((column): column is ColumnsType<ICompanyArticle>[number] => !!column),
      ...(actionColumnWithSettings ? [actionColumnWithSettings] : []),
    ];
    const [selectedArticleIds, setSelectedArticleIds] = useState<React.Key[]>([]);
    useImperativeHandle(ref, () => ({
      refreshList: (stayCurrent = false) => {
        if (!stayCurrent) {
          resetToFirstPageOrRefresh();
        } else {
          refreshArticleList();
        }
      },
    }));
    return (
      <Flex vertical gap={20}>
        <Flex gap={8} align="center" justify="space-between">
          <Flex gap={8} align="center">
            {btns}
            {selectedArticleIds.length > 0 && (
              <>
                <Button
                  type="primary"
                  onClick={async () => {
                    const downloadKey = await articleApi.download(selectedArticleIds as string[]);
                    window.open(`/api/common/download/${downloadKey}`);
                    setSelectedArticleIds([]);
                  }}
                >
                  下载({selectedArticleIds.length})
                </Button>
                <AccessButton permission={['company:article:delete']}>
                  <Button
                    type="primary"
                    onClick={() => {
                      openConfirmModal(
                        {
                          content: `确定批量发布选中的${selectedArticleIds.length}篇文章吗？`,
                          confirmApi: () => articleApi.publish(selectedArticleIds as string[]),
                        },
                        undefined,
                        () => {
                          message.success('批量发布成功');
                          resetToFirstPageOrRefresh();
                          setSelectedArticleIds([]);
                        }
                      );
                    }}
                  >
                    发布({selectedArticleIds.length})
                  </Button>
                </AccessButton>
                <AccessButton permission={['company:article:publish']}>
                  <Button
                    type="primary"
                    onClick={() => {
                      openConfirmModal(
                        {
                          content: `确定将选中的${selectedArticleIds.length}篇文章批量发布至客户后台吗？`,
                          confirmApi: () => articleApi.publishToClient(selectedArticleIds as string[]),
                        },
                        undefined,
                        () => {
                          message.success('批量发布至客户后台成功');
                          resetToFirstPageOrRefresh();
                          setSelectedArticleIds([]);
                        }
                      );
                    }}
                  >
                    发布客户后台({selectedArticleIds.length})
                  </Button>
                </AccessButton>
                <AccessButton permission={['company:article:delete']}>
                  <Button
                    type="primary"
                    danger
                    onClick={() => {
                      openDeleteModal(
                        {
                          content: `确定删除选中的${selectedArticleIds.length}篇文章吗？`,
                          api: () => articleApi.delete(selectedArticleIds as string[]),
                        },
                        undefined,
                        () => {
                          resetToFirstPageOrRefresh();
                          setSelectedArticleIds([]);
                        }
                      );
                    }}
                  >
                    删除({selectedArticleIds.length})
                  </Button>
                </AccessButton>
              </>
            )}
          </Flex>
          <SearchInput
            style={{ width: 260, marginLeft: 'auto' }}
            placeholder="搜索文章名称/企业名称/媒体名称"
            defaultValue={articleParams.search}
            onSearch={(val) => {
              setArticleParams({ search: val, pageIndex: 1 });
            }}
          />
        </Flex>
        <CommonTable<ICompanyArticle>
          loading={loading}
          rowKey={'articleId'}
          columns={displayColumns}
          dataSource={articleList?.records || []}
          total={articleList?.total}
          params={articleParams}
          setParams={setArticleParams}
          rowSelection={{
            preserveSelectedRowKeys: true,
            selectedRowKeys: selectedArticleIds,
            onChange: (selectedRowKeys) => {
              setSelectedArticleIds(selectedRowKeys);
            },
          }}
          onChange={(
            _pagination: TablePaginationConfig,
            filters: Record<string, FilterValue | null>,
            _sorter: SorterResult<ICompanyArticle> | SorterResult<ICompanyArticle>[]
          ) => {
            const nextStatus = ((filters.status as React.Key[] | undefined) || []).map((item) => String(item)) as ArticleStatus[];
            const nextPublishStatus = ((filters.publishStatus as React.Key[] | undefined) || []).map((item) =>
              String(item)
            ) as EPublishStatus[];
            const currentStatus = articleParams.status || [];
            const currentPublishStatus = articleParams.publishStatus || [];
            const isSameStatus =
              nextStatus.length === currentStatus.length && nextStatus.every((status, index) => status === currentStatus[index]);
            const isSamePublishStatus =
              nextPublishStatus.length === currentPublishStatus.length &&
              nextPublishStatus.every((status, index) => status === currentPublishStatus[index]);
            if (isSameStatus && isSamePublishStatus) {
              return;
            }
            setArticleParams({
              status: nextStatus,
              publishStatus: nextPublishStatus,
              pageIndex: 1,
            });
          }}
          scroll={{ x: 1200 }}
        />
      </Flex>
    );
  }
);
export default ArticleList;
export const openEditArticleModal = CommonModalFactory<{
  content: string;
  articleId?: string;
  companyId: string;
  mediaId?: string;
  mediaName?: string;
  title?: string;
  publishUrl?: string;
  status?: ArticleStatus;
}>(
  {
    title: '编辑文章',
    width: 1000,
  },
  ({ data, setOkFn }) => {
    const [form] = Form.useForm();
    const [mediaId, setMediaId] = useState(data?.mediaId || '');
    const [imagePickerOpen, setImagePickerOpen] = useState(false);
    const [selectedImageIds, setSelectedImageIds] = useState<string[]>([]);
    const [visibleImageCount, setVisibleImageCount] = useState(IMAGE_RENDER_BATCH_SIZE);
    const [uploading, setUploading] = useState(false);
    const [imageMenu, setImageMenu] = useState<ArticleImageMenu | null>(null);
    const cherryInstance = useRef<any>();
    const imagePreviewMarkers = useRef<any[]>([]);
    const markdownContainerRef = useRef<HTMLDivElement>(null);
    const draggingImageBlockRef = useRef<{
      block: ArticleImageBlock;
      startX: number;
      startY: number;
      moved: boolean;
      target: HTMLElement;
    } | null>(null);
    const resizingImageBlockRef = useRef<{
      block: ArticleImageBlock;
      startX: number;
      startWidth: number;
      image: HTMLImageElement;
    } | null>(null);
    const getMarkdown = () => cherryInstance.current?.getMarkdown?.() || '';
    const openImageOperationMenu = (block: ArticleImageBlock, target: HTMLElement) => {
      const containerRect = markdownContainerRef.current?.getBoundingClientRect();
      const targetRect = target.getBoundingClientRect();
      if (!containerRect) {
        return;
      }
      setImageMenu({
        block,
        left: Math.max(12, targetRect.left - containerRect.left),
        top: Math.max(12, targetRect.bottom - containerRect.top + 8),
      });
    };
    const handleImageMouseDown = (block: ArticleImageBlock, event: MouseEvent) => {
      if (event.button !== 0) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      draggingImageBlockRef.current = {
        block,
        startX: event.clientX,
        startY: event.clientY,
        moved: false,
        target: event.currentTarget as HTMLElement,
      };
      setImageMenu(null);
      document.body.classList.add(styles.articleImageDraggingBody);
    };
    const handleImageResizeMouseDown = (block: ArticleImageBlock, image: HTMLImageElement, event: MouseEvent) => {
      if (event.button !== 0) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      resizingImageBlockRef.current = {
        block,
        startX: event.clientX,
        startWidth: image.getBoundingClientRect().width,
        image,
      };
      setImageMenu(null);
      image.closest(`.${styles.articleEditorImagePreview}`)?.classList.add(styles.articleEditorImagePreviewResizing);
      document.body.classList.add(styles.articleImageResizingBody);
    };
    const clearImageDragState = () => {
      draggingImageBlockRef.current = null;
      markdownContainerRef.current
        ?.querySelectorAll(`.${styles.articleEditorImagePreviewDragging}`)
        .forEach((node) => node.classList.remove(styles.articleEditorImagePreviewDragging));
      markdownContainerRef.current?.classList.remove(styles.markdownContainerDragOver);
      document.body.classList.remove(styles.articleImageDraggingBody);
    };
    const clearImageResizeState = () => {
      resizingImageBlockRef.current = null;
      markdownContainerRef.current
        ?.querySelectorAll(`.${styles.articleEditorImagePreviewResizing}`)
        .forEach((node) => node.classList.remove(styles.articleEditorImagePreviewResizing));
      document.body.classList.remove(styles.articleImageResizingBody);
    };
    const moveImageBlockToPosition = (block: ArticleImageBlock, position: { line: number; ch: number }) => {
      const current = getMarkdown();
      const blockStart = current.indexOf(block.block);
      if (blockStart < 0) {
        return;
      }
      const blockEnd = blockStart + block.block.length;
      const targetIndex = getMarkdownLineStartIndex(current, position.line);
      if (targetIndex >= blockStart && targetIndex <= blockEnd) {
        return;
      }
      const withoutBlock = current.slice(0, blockStart) + current.slice(blockEnd);
      const insertIndex = targetIndex > blockStart ? Math.max(0, targetIndex - block.block.length) : targetIndex;
      setMarkdown(insertImageBlockAtIndex(withoutBlock, block.block, insertIndex));
    };
    const resizeImageBlock = (block: ArticleImageBlock, width: number) => {
      replaceImageBlock(
        block.block,
        buildArticleImageBlock(
          {
            imageUrl: block.src,
            imageName: block.alt,
          },
          block.align,
          block.size,
          width
        )
      );
    };
    const refreshEditorImagePreviews = (markdown = getMarkdown()) => {
      imagePreviewMarkers.current.forEach((marker) => marker?.clear?.());
      imagePreviewMarkers.current = [];
      const codeMirror = cherryInstance.current?.getCodeMirror?.();
      if (!codeMirror?.markText) {
        return;
      }
      parseArticleImageBlocks(markdown).forEach((block) => {
        const marker = codeMirror.markText(block.from, block.to, {
          replacedWith: createEditorImagePreviewNode(block, handleImageMouseDown, handleImageResizeMouseDown),
          clearOnEnter: false,
          atomic: true,
          handleMouseEvents: true,
        });
        imagePreviewMarkers.current.push(marker);
      });
    };
    const setMarkdown = (markdown: string) => {
      const instance = cherryInstance.current;
      if (instance?.setMarkdown) {
        instance.setMarkdown(markdown);
      } else if (instance?.setValue) {
        instance.setValue(markdown);
      } else if (instance?.editor?.setMarkdown) {
        instance.editor.setMarkdown(markdown);
      } else if (instance?.editor?.setValue) {
        instance.editor.setValue(markdown);
      } else if (instance?.editor?.editor?.setValue) {
        instance.editor.editor.setValue(markdown);
      } else {
        message.warning('编辑器暂不支持自动更新内容，请手动调整图片源码');
      }
      setImageMenu(null);
      window.setTimeout(() => refreshEditorImagePreviews(markdown), 0);
    };
    const {
      data: images = [],
      run: refreshImages,
      loading: imageLoading,
    } = useRequest(() => companyApi.listImages(data?.companyId || ''), {
      ready: !!data?.companyId,
      refreshDeps: [data?.companyId],
    });
    const selectedImages = useMemo(
      () => images.filter((image) => selectedImageIds.includes(image.imageId)),
      [images, selectedImageIds]
    );
    const visibleImages = images.slice(0, visibleImageCount);
    const hasMoreImages = visibleImageCount < images.length;
    const loadMoreImages = () => {
      setVisibleImageCount((count) => Math.min(count + IMAGE_RENDER_BATCH_SIZE, images.length));
    };
    const handleImageGridScroll = (event: UIEvent<HTMLDivElement>) => {
      const target = event.currentTarget;
      if (hasMoreImages && target.scrollHeight - target.scrollTop - target.clientHeight < 24) {
        loadMoreImages();
      }
    };
    const isAllImagesSelected = images.length > 0 && selectedImageIds.length === images.length;
    const toggleImageSelection = (imageId: string) => {
      setSelectedImageIds((ids) => (ids.includes(imageId) ? ids.filter((id) => id !== imageId) : [...ids, imageId]));
    };

    const insertMarkdown = (snippet: string) => {
      const content = snippet.trim();
      const codeMirror = cherryInstance.current?.getCodeMirror?.();
      const doc = codeMirror?.getDoc?.() || codeMirror;
      if (codeMirror?.getValue && doc?.replaceRange && doc?.getCursor) {
        const from = doc.getCursor('from');
        const to = doc.getCursor('to');
        const current = codeMirror.getValue();
        const startIndex = codeMirror.indexFromPos?.(from) ?? current.length;
        const endIndex = codeMirror.indexFromPos?.(to) ?? startIndex;
        const insertText = buildMarkdownInsertion(current, startIndex, endIndex, content);
        doc.replaceRange(insertText, from, to);
        const nextCursor = codeMirror.posFromIndex?.(startIndex + insertText.length);
        if (nextCursor) {
          doc.setCursor?.(nextCursor);
        }
        codeMirror.focus?.();
        setImageMenu(null);
        window.setTimeout(() => refreshEditorImagePreviews(codeMirror.getValue()), 0);
      } else if (codeMirror?.replaceSelection && codeMirror?.getValue) {
        const cursor = codeMirror.getCursor?.('from') || codeMirror.getCursor?.();
        const current = codeMirror.getValue();
        const startIndex = cursor && codeMirror.indexFromPos ? codeMirror.indexFromPos(cursor) : current.length;
        const insertText = buildMarkdownInsertion(current, startIndex, startIndex, content);
        codeMirror.replaceSelection(insertText);
        codeMirror.focus?.();
        setImageMenu(null);
        window.setTimeout(() => refreshEditorImagePreviews(codeMirror.getValue()), 0);
      } else {
        const current = getMarkdown();
        const next = `${current}${current.trim() ? '\n\n' : ''}${content}\n`;
        setMarkdown(next);
      }
      setImagePickerOpen(false);
      setSelectedImageIds([]);
    };

    const insertSelectedImages = () => {
      if (!selectedImages.length) {
        message.warning('请先选择图片');
        return;
      }
      insertMarkdown(selectedImages.map((image) => buildArticleImageBlock(image)).join('\n\n'));
    };

    const replaceImageBlock = (block: string, nextBlock: string) => {
      setMarkdown(getMarkdown().replace(block, nextBlock));
    };

    const updateImageBlock = (block: ArticleImageBlock, patch: Partial<Pick<ArticleImageBlock, 'align' | 'size'>>) => {
      const nextSize = patch.size || block.size;
      replaceImageBlock(
        block.block,
        buildArticleImageBlock(
          {
            imageUrl: block.src,
            imageName: block.alt,
          },
          patch.align || block.align,
          nextSize,
          patch.size ? undefined : block.width
        )
      );
    };

    const removeImageBlock = (block: ArticleImageBlock) => {
      setMarkdown(getMarkdown().replace(block.block, '').replace(/\n{3,}/g, '\n\n'));
    };

    const beforeUploadImage = async (file: File) => {
      if (!data.companyId) {
        message.warning('缺少企业信息，暂不能上传图片');
        return Upload.LIST_IGNORE;
      }
      const ext = file.name.split('.').pop()?.toLowerCase();
      if (!ext || !['jpg', 'jpeg', 'png'].includes(ext)) {
        message.warning('仅支持 JPG、JPEG、PNG 格式图片');
        return Upload.LIST_IGNORE;
      }
      if (file.size > MAX_IMAGE_SIZE) {
        message.warning('单张图片大小不能超过10MB');
        return Upload.LIST_IGNORE;
      }
      try {
        setUploading(true);
        const image = await companyApi.uploadImage({ companyId: data.companyId, file });
        message.success('上传成功');
        await refreshImages();
        if (image?.imageId) {
          setSelectedImageIds((ids) => Array.from(new Set([...ids, image.imageId])));
        }
      } finally {
        setUploading(false);
      }
      return false;
    };

    const imagePickerContent = (
      <div className={styles.articleEditImagePicker}>
        <div className={styles.articleEditImagePickerHeader}>
          <span>
            企业图片库
            <InfoCircleOutlined />
          </span>
          <Space size={8}>
            <Upload accept={ACCEPT_IMAGE_TYPES} showUploadList={false} beforeUpload={beforeUploadImage}>
              <Button type="link" size="small" icon={<UploadOutlined />} loading={uploading}>
                上传图片
              </Button>
            </Upload>
            <Button
              type="link"
              size="small"
              icon={<CheckSquareOutlined />}
              disabled={!images.length}
              onClick={() => setSelectedImageIds(isAllImagesSelected ? [] : images.map((image) => image.imageId))}
            >
              全选
            </Button>
            <Button
              type="link"
              size="small"
              danger
              disabled={!selectedImageIds.length}
              onClick={() => setSelectedImageIds([])}
            >
              清空
            </Button>
          </Space>
        </div>
        {imageLoading ? (
          <div className={styles.articleEditImageEmpty}>加载中...</div>
        ) : images.length ? (
          <>
            <div className={styles.articleEditImageGrid} onScroll={handleImageGridScroll}>
              {visibleImages.map((image) => (
                <button
                  key={image.imageId}
                  type="button"
                  className={`${styles.articleEditImageOption} ${
                    selectedImageIds.includes(image.imageId) ? styles.articleEditImageOptionSelected : ''
                  }`}
                  onClick={() => toggleImageSelection(image.imageId)}
                  title={image.imageName}
                >
                  <img
                    src={image.thumbnailUrl || image.imageUrl}
                    alt={image.imageName}
                    loading="lazy"
                    decoding="async"
                  />
                </button>
              ))}
            </div>
            {hasMoreImages && (
              <Button block className={styles.articleEditImageMore} onClick={loadMoreImages}>
                查看更多
              </Button>
            )}
          </>
        ) : (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无图片" />
        )}
        <div className={styles.articleEditImagePickerFooter}>
          <span>已选择 {selectedImageIds.length} 张图片</span>
          <Button type="primary" size="small" disabled={!selectedImageIds.length} onClick={insertSelectedImages}>
            插入
          </Button>
        </div>
      </div>
    );

    useEffect(() => {
      setVisibleImageCount(IMAGE_RENDER_BATCH_SIZE);
    }, [data?.companyId]);

    useEffect(() => {
      form.setFieldsValue({
        title: data?.title,
        publishUrl: data?.publishUrl,
        status: data?.status,
      });
      cherryInstance.current = new window.Cherry({
        ...markdownConfig,
        value: data?.content,
        event: {
          afterChange: () => {
            setImageMenu(null);
            window.setTimeout(() => refreshEditorImagePreviews(), 0);
          },
        },
      } as any);
      window.setTimeout(() => refreshEditorImagePreviews(data?.content || ''), 0);
      return () => {
        imagePreviewMarkers.current.forEach((marker) => marker?.clear?.());
        imagePreviewMarkers.current = [];
      };
    }, [data?.content, data?.publishUrl, data?.title, data?.status, form]);
    useEffect(() => {
      const handleMouseMove = (event: MouseEvent) => {
        const resizing = resizingImageBlockRef.current;
        if (resizing) {
          const nextWidth = Math.round(Math.min(900, Math.max(120, resizing.startWidth + event.clientX - resizing.startX)));
          resizing.image.style.width = `${nextWidth}px`;
          return;
        }
        const dragging = draggingImageBlockRef.current;
        if (!dragging) {
          return;
        }
        const distance = Math.abs(event.clientX - dragging.startX) + Math.abs(event.clientY - dragging.startY);
        if (distance > 4) {
          dragging.moved = true;
          dragging.target.classList.add(styles.articleEditorImagePreviewDragging);
          markdownContainerRef.current?.classList.add(styles.markdownContainerDragOver);
        }
      };
      const handleMouseUp = (event: MouseEvent) => {
        const resizing = resizingImageBlockRef.current;
        if (resizing) {
          const nextWidth = Math.round(Math.min(900, Math.max(120, resizing.startWidth + event.clientX - resizing.startX)));
          resizeImageBlock(resizing.block, nextWidth);
          clearImageResizeState();
          return;
        }
        const dragging = draggingImageBlockRef.current;
        if (!dragging) {
          return;
        }
        if (dragging.moved) {
          const codeMirror = cherryInstance.current?.getCodeMirror?.();
          if (codeMirror?.coordsChar) {
            const position = codeMirror.coordsChar({ left: event.clientX, top: event.clientY }, 'window');
            moveImageBlockToPosition(dragging.block, position);
          }
        } else {
          openImageOperationMenu(dragging.block, dragging.target);
        }
        clearImageDragState();
      };
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.body.classList.remove(styles.articleImageDraggingBody);
        document.body.classList.remove(styles.articleImageResizingBody);
      };
    }, []);
    useEffect(() => {
      setOkFn(async () => {
        const values = await form.validateFields();
        let markdown = cherryInstance.current?.getMarkdown();
        await articleApi.update({
          articleId: data?.articleId as string,
          content: markdown || '',
          mediaId,
          title: values.title,
          publishUrl: values.publishUrl,
          status: values.status,
        });
      });
    }, [mediaId, data, form, setOkFn]);
    return (
      <Form form={form}>
        <Flex vertical gap={8}>
          <Form.Item name="title" label="文章标题" rules={[{ required: true, message: '请输入文章标题' }]}>
            <Input placeholder="请输入文章标题" />
          </Form.Item>
          <Form.Item name="publishUrl" label="媒体链接">
            <Input placeholder="请输入媒体链接" />
          </Form.Item>
          <div className={styles.articleEditMetadata}>
            <Form.Item label="媒体" className={styles.articleEditMedia}>
              <ChannelMediaBoxSelector
                defaultOptions={[
                  {
                    value: data?.mediaId || '',
                    label: data?.mediaName || '',
                  },
                ]}
                value={mediaId}
                onChange={setMediaId}
              />
            </Form.Item>
            <Form.Item
              name="status"
              label="文章状态"
              className={styles.articleEditStatus}
              rules={[{ required: true, message: '请选择文章状态' }]}
            >
              <Select
                placeholder="请选择文章状态"
                options={Object.entries(articleStatusMap).map(([value, label]) => ({ value, label }))}
              />
            </Form.Item>
          </div>
          <div
            ref={markdownContainerRef}
            className={styles.markdownContainer}
            onClick={() => setImageMenu(null)}
          >
            <Popover
              trigger="click"
              placement="bottomRight"
              open={imagePickerOpen}
              onOpenChange={setImagePickerOpen}
              content={imagePickerContent}
              overlayClassName={styles.articleEditImagePopover}
            >
              <Button className={styles.articleEditImageEntry} icon={<PictureOutlined />}>
                图片
              </Button>
            </Popover>
            {imageMenu && (
              <div
                className={styles.articleImageOperationMenu}
                style={{ left: imageMenu.left, top: imageMenu.top }}
                onClick={(event) => event.stopPropagation()}
              >
                <Tooltip title="删除图片">
                  <Button
                    size="small"
                    icon={<DeleteOutlined />}
                    onClick={() => removeImageBlock(imageMenu.block)}
                  />
                </Tooltip>
                <Tooltip title="左对齐">
                  <Button
                    size="small"
                    type={imageMenu.block.align === 'left' ? 'primary' : 'default'}
                    icon={<AlignLeftOutlined />}
                    onClick={() => updateImageBlock(imageMenu.block, { align: 'left' })}
                  />
                </Tooltip>
                <Tooltip title="居中对齐">
                  <Button
                    size="small"
                    type={imageMenu.block.align === 'center' ? 'primary' : 'default'}
                    icon={<AlignCenterOutlined />}
                    onClick={() => updateImageBlock(imageMenu.block, { align: 'center' })}
                  />
                </Tooltip>
                <Tooltip title="右对齐">
                  <Button
                    size="small"
                    type={imageMenu.block.align === 'right' ? 'primary' : 'default'}
                    icon={<AlignRightOutlined />}
                    onClick={() => updateImageBlock(imageMenu.block, { align: 'right' })}
                  />
                </Tooltip>
                {(['small', 'medium', 'large', 'original'] as ArticleImageSize[]).map((size) => (
                  <Button
                    key={size}
                    size="small"
                    type={imageMenu.block.size === size ? 'primary' : 'default'}
                    onClick={() => updateImageBlock(imageMenu.block, { size })}
                  >
                    {imageSizeText[size].replace('尺寸', '')}
                  </Button>
                ))}
              </div>
            )}
            <div id="markdown-container" className={styles.markdown}></div>
          </div>
        </Flex>
      </Form>
    );
  },
  async ({ articleId, ...props }) => {
    const res = await articleApi.get(articleId!);
    return {
      ...props,
      content: res.content,
      articleId,
      companyId: res.companyId,
      mediaId: res.mediaId,
      mediaName: res.mediaName,
      title: res.title,
      publishUrl: res.publishUrl,
      status: res.status,
    };
  }
);
